import React from 'react';
import T from 'i18n';
import { ClickAwayListener, MenuItem, Paper, Popper, Typography } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useUpdateUserSettings } from 'features/common/redux/updateUserSettings';
import { AccessTime } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId } from 'common/selectors';
import { removeFormatSuffix } from './utils/formatUtils';

interface RecentSearchesListProps {
  anchorEl: any;
  openRecentSearchesMenu: boolean;
  handleRecentSearchesMenuClose: any;
  isAnswersMode?: boolean;
  topic?: string;
  handleChangeFilters: Function;
  currentInput?: string;
}

const RecentSearchesList = ({
  anchorEl,
  openRecentSearchesMenu,
  handleRecentSearchesMenuClose,
  isAnswersMode,
  topic,
  handleChangeFilters,
  currentInput = '',
}: RecentSearchesListProps) => {
  const { userSettings } = useUpdateUserSettings();
  const currentCaseId = useSelector(selectCurrentCaseId);
  const aiInputs = userSettings?.aiSearchInputs || [];
  const aiSearchInputs = aiInputs[currentCaseId] ? aiInputs[currentCaseId] : [];
  const allRecentSearches = isAnswersMode
    ? aiSearchInputs.find((itm: any) => itm.topic === topic)?.questions || []
    : aiSearchInputs.map((item: any) => item.topic);
  
  // Filter recent searches based on current input
  const filteredSearches = allRecentSearches.filter((search: string) =>
    search && 
    typeof search === 'string' && 
    search.toLowerCase().includes(currentInput.toLowerCase())
  );
  
  // Remove case-insensitive duplicates, prioritizing uppercase starting versions
  const recentSearches = filteredSearches.reduce((acc: string[], current: string) => {
    const existingIndex = acc.findIndex(item => 
      item.toLowerCase() === current.toLowerCase()
    );
    
    if (existingIndex === -1) {
      // No duplicate found, add the current item
      acc.push(current);
    } else {
      // Duplicate found, keep the one that starts with uppercase
      const existing = acc[existingIndex];
      const currentStartsWithUpper = /^[A-Z]/.test(current);
      const existingStartsWithUpper = /^[A-Z]/.test(existing);
      
      if (currentStartsWithUpper && !existingStartsWithUpper) {
        // Replace existing with current (uppercase priority)
        acc[existingIndex] = current;
      }
      // If both start with uppercase or both start with lowercase, keep existing
    }
    
    return acc;
  }, []);
  return (
    <ClickAwayListener onClickAway={handleRecentSearchesMenuClose}>
      <Popper
        id="recent-searches-menu"
        anchorEl={anchorEl}
        open={openRecentSearchesMenu && recentSearches.length > 0}
        placement="top-start"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 15], // [horizontal, vertical] offset in pixels
            },
          },
        ]}
      >
        <Paper elevation={3} sx={{ width: '36rem' }}>
          <PerfectScrollbar options={{ suppressScrollX: true }} style={{ maxHeight: '30vh' }}>
            <Typography sx={{ color: '#0000007a', mt: '0.5rem', ml: '1.5rem' }}>
              {T.translate('case.recentSearchesMsg')}
            </Typography>
            {recentSearches.map((item: any, index: number) => (
              <MenuItem
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleChangeFilters({ phrase: item });
                  handleRecentSearchesMenuClose();
                }}
              >
                <AccessTime fontSize="medium" sx={{ mr: '0.5rem' }} />
                <Typography sx={{ fontWeight: 600, color: '#0000007a' }}>{removeFormatSuffix(item)}</Typography>
              </MenuItem>
            ))}
          </PerfectScrollbar>
        </Paper>
      </Popper>
    </ClickAwayListener>
  );
};

export default RecentSearchesList;
